import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, Home, Clock, Bookmark, BookmarkCheck, X, AlertCircle } from 'lucide-react';
import AnswerCard from './components/AnswerCard';
import QuestionRenderer from './components/QuestionRenderer';
import { supabase, getSupabaseUrl } from '../../supabase/client';
import type { Question, Paper, FavoriteQuestion } from './types';
import { typeNames, subTypeNames } from '../../constants/questionTypes';
import { categoryStore } from '../../store/categoryStore';
import QuizQuestionTitle from '../../components/admin/QuizQuestionTitle';
import PreviewControls from './components/PreviewControls';
import { useAutoSave } from '../../services/AutoSaveService';
import type { AutoSaveData } from '../../services/AutoSaveService';
import { AutoSaveStatusIndicatorInline } from '../../components/AutoSaveStatusIndicator';
import { saveFirstAttempts } from '../../utils/questionStats';

const EXAM_DURATION = 60 * 60;

interface QuizAnswerModeProps {
  paper: Paper;
  examQuestions: Question[];
  currentUser: any;
  paperId?: string;
  isPreview?: boolean;
  isEditorPreview?: boolean;
  canScaleImages?: boolean; // 是否允许点击图片缩放，默认与isEditorPreview一致
  onBack: () => void;
  // 编辑器预览模式：将题目变更（图片宽度、选项布局等）同步回 PaperEditor 的 state
  // 仅在 isEditorPreview=true 时使用，避免预览弹窗内的修改丢失
  onExamQuestionsChange?: (questions: Question[]) => void;
}

export default function QuizAnswerMode({
  paper,
  examQuestions: initialQuestions,
  currentUser,
  paperId,
  isPreview = false,
  isEditorPreview = false,
  canScaleImages: propCanScaleImages,
  onBack,
  onExamQuestionsChange
}: QuizAnswerModeProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const examKey = `exam_${paperId || 'custom'}`;

  // 编程题 HTML 清洗职责已收敛到 QuizQuestionTitle 内部，此处直接使用原始数据
  const [examQuestions, setExamQuestionsState] = useState<Question[]>(initialQuestions);

  // 关键修复：使用 ref 保存最新的 examQuestions，避免闭包陷阱
  // handleSaveWidthSettings 中通过 ref 读取最新值，确保保存时包含所有修改
  const examQuestionsRef = useRef(examQuestions);
  useEffect(() => {
    examQuestionsRef.current = examQuestions;
  }, [examQuestions]);

  // 注意：不再监听 initialQuestions 变化来重置内部 state
  // 原因：编辑器预览模式下，用户修改会通过 onExamQuestionsChange 异步通知父组件，
  // 父组件重新渲染后传入新的 initialQuestions，如果此时重置 state 会导致竞态条件：
  // 先改题干→触发父组件更新→父组件传回props→useEffect重置state（丢失题干修改）→再改选项→只保留选项
  // 内部 state 应完全由用户操作驱动，仅在组件首次挂载时使用 initialQuestions

  // 包装 setExamQuestions：编辑器预览模式下，将题目变更同步回 PaperEditor 的 state
  // 这样预览弹窗内调整的图片宽度、选项布局等关闭后能保留（所见即所得）
  const setExamQuestions = useCallback((updater: Question[] | ((prev: Question[]) => Question[])) => {
    setExamQuestionsState(prev => {
      const next = typeof updater === 'function' ? (updater as (p: Question[]) => Question[])(prev) : updater;
      // 关键修复：同步更新 ref，避免 useEffect 异步执行导致 ref 滞后
      // 这样 handleSaveWidthSettings 读取 ref 时能拿到最新值
      examQuestionsRef.current = next;
      if (isEditorPreview && onExamQuestionsChange) {
        // 异步触发父组件更新，避免在渲染过程中调用 setState
        // 使用微任务确保当前渲染周期完成后再同步
        Promise.resolve().then(() => onExamQuestionsChange(next));
      }
      return next;
    });
  }, [isEditorPreview, onExamQuestionsChange]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [timeLeft, setTimeLeft] = useState(EXAM_DURATION);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [score, setScore] = useState(0);
  const [markedQuestions, setMarkedQuestions] = useState<Set<number>>(new Set());
  const [showSidebar, setShowSidebar] = useState(false);
  const [favorites, setFavorites] = useState<FavoriteQuestion[]>([]);
  const [answerMode, setAnswerMode] = useState<'upload' | 'online'>('online');
  const [isAssignmentMode, setIsAssignmentMode] = useState(false);
  const [hasWidthChanges, setHasWidthChanges] = useState(false);
  const [isSavingWidths, setIsSavingWidths] = useState(false);
  const [initialWidths, setInitialWidths] = useState<Record<string, { titleImageWidths?: number[]; optionImageWidths?: { id: string; imageWidths?: number[] }[] }>>({});
  const [restoredFromCache, setRestoredFromCache] = useState(false);
  const [showPaperSettingsModal, setShowPaperSettingsModal] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);
  const [isSavingAnswer, setIsSavingAnswer] = useState(false);

  // 是否启用图片缩放交互：优先使用传入的canScaleImages prop，否则回退到isEditorPreview
  // 试卷编辑页面预览时传canScaleImages={true}，其他预览页面不传或传false
  const canScaleImages = propCanScaleImages ?? isEditorPreview;

  const currentQuestion = examQuestions[currentIndex];

  // 当题目列表为空或当前题目不存在时，显示空状态
  if (!currentQuestion) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-600 mb-2">暂无题目</h2>
          <p className="text-gray-500 mb-6">试卷中没有可显示的问题</p>
          <button
            onClick={onBack}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            返回
          </button>
        </div>
      </div>
    );
  }

  const answeredCount = Object.keys(answers).filter(key => !key.includes('_')).length;
  const isTimeWarning = timeLeft < 600;

  // ============ 自动保存服务 ============

  // 数据采集函数：收集当前答题状态
  const collectAutoSaveData = useCallback((): AutoSaveData => {
    return {
      answers,
      currentIndex,
      timeLeft,
      markedQuestions: Array.from(markedQuestions),
      isSubmitted,
      score,
      savedAt: new Date().toISOString(),
    };
  }, [answers, currentIndex, timeLeft, markedQuestions, isSubmitted, score]);

  // 保存函数：将数据提交到 exam_progress 表
  const saveToServer = useCallback(async (data: AutoSaveData): Promise<boolean> => {
    if (!currentUser?.id || isPreview) return false;
    try {
      const progress = {
        user_id: currentUser.id,
        exam_id: examKey,
        answers: data.answers,
        current_index: data.currentIndex,
        time_left: data.timeLeft,
        marked_questions: data.markedQuestions,
        is_submitted: data.isSubmitted,
        score: data.score,
        saved_at: data.savedAt,
      };
      const { data: existing } = await supabase.from('exam_progress').select('id').eq('user_id', currentUser.id).eq('exam_id', examKey).maybeSingle();
      if (existing) {
        const { error } = await supabase.from('exam_progress').update(progress).eq('id', existing.id);
        if (error) return false;
      } else {
        const { error } = await supabase.from('exam_progress').insert(progress);
        if (error) return false;
      }
      return true;
    } catch {
      return false;
    }
  }, [currentUser?.id, examKey, isPreview]);

  // 使用自动保存 Hook
  const { statusInfo, triggerDebouncedSave, saveNow, clearLocalCache, resetAutoSave } = useAutoSave(
    examKey,
    collectAutoSaveData,
    saveToServer
  );

  // ============ 加载收藏和考试进度 ============

  useEffect(() => {
    if (!currentUser?.id || isPreview) return;

    const loadData = async () => {
      // 加载收藏
      const { data: favData } = await supabase.from('user_favorites').select('*').eq('user_id', currentUser.id);
      if (favData) {
        const mappedFavorites: FavoriteQuestion[] = favData.map((f: any) => ({
          id: f.id,
          questionId: f.question_id,
          content: '',
          category: f.category_code || '',
          level_text: f.level_number ? String(f.level_number) : '',
          correctAnswer: '',
          explanation: '',
          addTime: f.created_at,
          paperId: f.paper_id,
          paperTitle: ''
        }));
        setFavorites(mappedFavorites);
      }

      // 先检查是否已有提交记录，如有则不恢复任何进度
      const { count: submittedCount } = await supabase.from('exam_records').select('*', { count: 'exact' }).eq('user_id', currentUser.id).eq('paper_id', paperId || 'custom');
      const hasSubmitted = (submittedCount || 0) > 0;

      // 加载考试进度（从数据库）- 仅在未提交过时恢复
      if (!hasSubmitted) {
        const { data: progressData } = await supabase.from('exam_progress').select('*').eq('user_id', currentUser.id).eq('exam_id', examKey).maybeSingle();
        if (progressData) {
          const savedAt = new Date(progressData.saved_at);
          const now = new Date();
          const hoursDiff = (now.getTime() - savedAt.getTime()) / (1000 * 60 * 60);
          if (progressData.is_submitted && hoursDiff <= 48) {
            setIsSubmitted(true);
            setScore(progressData.score || 0);
            if (progressData.answers) setAnswers(progressData.answers as Record<string, string>);
            if (progressData.current_index) setCurrentIndex(progressData.current_index);
          } else if (!progressData.is_submitted) {
            // 未提交的进度：恢复答题状态
            if (progressData.answers) setAnswers(progressData.answers as Record<string, string>);
            if (progressData.current_index) setCurrentIndex(progressData.current_index);
          }
          if (progressData.marked_questions) setMarkedQuestions(new Set(progressData.marked_questions));
          if (progressData.time_left) setTimeLeft(progressData.time_left);
        }
      }

      // 加载答题模式设置
      const { data: examSettings } = await supabase.from('exam_settings').select('answer_mode').single();
      if (examSettings) {
        const paperAnswerMode = paper?.answer_mode as 'upload' | 'online' | undefined;
        setAnswerMode(paperAnswerMode || (examSettings.answer_mode as 'upload' | 'online') || 'online');
      }

      // 检查是否是作业模式（URL中有assignmentId参数）
      const searchParams = new URLSearchParams(location.search);
      const assignmentId = searchParams.get('assignmentId');
      setIsAssignmentMode(!!assignmentId);

      // 如果没有提交记录（重新开始答题），重置自动保存状态
      if (!hasSubmitted) {
        resetAutoSave();
      }
    };

    loadData();
  }, [currentUser?.id, examKey, isPreview, resetAutoSave]);

  // ============ 页面刷新恢复逻辑 ============
  // 如果数据库中没有进度数据，尝试从localStorage恢复
  useEffect(() => {
    if (!currentUser?.id || isPreview || restoredFromCache) return;

    // 仅在数据库进度未加载时尝试localStorage恢复
    // 这个逻辑在 loadData 完成后执行，如果 answers 仍为空则尝试恢复
    const tryRestoreFromCache = async () => {
      // 先检查数据库是否已有进度
      const { data: progressData } = await supabase.from('exam_progress').select('id').eq('user_id', currentUser.id).eq('exam_id', examKey).maybeSingle();
      if (progressData) return; // 数据库有数据，不需要从缓存恢复

      // 数据库无数据，尝试从localStorage恢复
      const cachedData = getLocalCacheData();
      if (cachedData && !cachedData.isSubmitted) {
        setAnswers(cachedData.answers);
        setCurrentIndex(cachedData.currentIndex);
        setTimeLeft(cachedData.timeLeft);
        setMarkedQuestions(new Set(cachedData.markedQuestions));
        setRestoredFromCache(true);
      }
    };

    tryRestoreFromCache();
  }, [currentUser?.id, examKey, isPreview, restoredFromCache]);

  // ============ 倒计时 ============

  useEffect(() => {
    if (isSubmitted || isPreview) return;
    if (timeLeft <= 0) {
      handleAutoSubmit();
      return;
    }
    const timer = setInterval(() => setTimeLeft(t => t - 1), 1000);
    return () => clearInterval(timer);
  }, [timeLeft, isSubmitted, isPreview]);

  // ============ 提交相关逻辑 ============

  const handleAutoSubmit = async () => {
    await confirmSubmit();
    alert('试卷已自动提交成功');
  };

  const calculateScore = () => {
    let totalScore = 0;
    examQuestions.forEach(q => {
      const answer = answers[q.id];
      if (!answer) return;
      if (q.type === 'multi_choice') {
        const selectedIds = answer.split(',');
        const correctIds = q.options?.filter(o => o.isCorrect).map(o => o.id) || [];
        if (selectedIds.length === correctIds.length && selectedIds.every(id => correctIds.includes(id))) {
          totalScore += q.score;
        }
      } else if (q.type === 'single_choice' || q.type === 'judge') {
        const selectedOption = q.options?.find(o => o.id === answer);
        if (selectedOption?.isCorrect) totalScore += q.score;
      }
    });
    return totalScore;
  };

  const confirmSubmit = async () => {
    if (isPreview) {
      alert('预览模式不能提交答案');
      return;
    }

    const finalScore = calculateScore();
    setScore(finalScore);
    setIsSubmitted(true);

    // 提交前先保存一次进度
    await saveNow();

    // 保存答题记录
    let examRecordId = null;
    if (currentUser?.id) {
      try {
        const { count } = await supabase.from('exam_records').select('*', { count: 'exact' }).eq('user_id', currentUser.id).eq('paper_id', paperId || 'custom');
        const submitCount = (count || 0) + 1;
        const totalScore = paper?.total_score || examQuestions.reduce((sum, q) => sum + q.score, 0);
        const isPassed = finalScore >= totalScore * 0.6;
        const timeUsed = EXAM_DURATION - timeLeft;

        const hasSubjectiveQuestions = examQuestions.some(q =>
          q.type === 'coding' || q.type === 'scratch_coding' || q.type === 'python_coding' || q.type === 'cpp_coding'
        );
        const isObjective = !hasSubjectiveQuestions;
        const gradingStatus = isObjective ? 'final' : 'pending';

        const objectiveTypes = ['single_choice', 'multi_choice', 'judge'];
        const objectiveScore = examQuestions.reduce((total, q) => {
          if (!objectiveTypes.includes(q.type)) return total;
          const userAnswer = answers[q.id];
          if (!userAnswer) return total;
          if (q.type === 'multi_choice') {
            const selectedIds = userAnswer.split(',');
            const correctIds = q.options?.filter(o => o.isCorrect).map(o => o.id) || [];
            if (selectedIds.length === correctIds.length && selectedIds.every(id => correctIds.includes(id))) {
              return total + (q.score || 0);
            }
          } else {
            const selectedOption = q.options?.find(o => o.id === userAnswer);
            if (selectedOption?.isCorrect) return total + (q.score || 0);
          }
          return total;
        }, 0);

        const objectiveTotalScore = examQuestions.reduce((total, q) => {
          if (objectiveTypes.includes(q.type)) {
            return total + (q.score || 0);
          }
          return total;
        }, 0);

        const programmingScore = null;

        const { data: examRecord } = await supabase.from('exam_records').insert({
          user_id: currentUser.id,
          paper_id: paperId || 'custom',
          paper_title: paper?.title || '自定义组卷',
          score: finalScore,
          total_score: totalScore,
          time_left: timeUsed,
          exam_duration: EXAM_DURATION,
          submit_count: submitCount,
          is_passed: isPassed,
          answers: answers as any,
          questions: examQuestions as any,
          is_objective: isObjective,
          grading_status: gradingStatus,
          objective_score: objectiveScore,
          objective_total_score: objectiveTotalScore,
          programming_score: programmingScore
        }).select().single();

        examRecordId = examRecord?.id;

        // 提交成功后删除exam_progress记录，确保重新进入时从初始状态开始
        await supabase.from('exam_progress').delete().eq('user_id', currentUser.id).eq('exam_id', examKey);

        // 提交成功后清除本地缓存
        clearLocalCache();

        // 检查是否是从作业入口进入的答题
        const searchParams = new URLSearchParams(location.search);
        const assignmentId = searchParams.get('assignmentId');

        if (assignmentId && examRecordId) {
          const session = (await supabase.auth.getSession()).data.session;
          if (session) {
            await fetch(`${getSupabaseUrl()}/functions/v1/exam-service`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${session.access_token}`,
              },
              body: JSON.stringify({
                action: 'complete_assignment',
                assignmentId,
                examRecordId,
                score: finalScore,
              }),
            });
          }
        }
      } catch (error) {
        console.error('保存答题记录失败:', error);
      }
    }

    // 自动记录错题
    try {
      const session = (await supabase.auth.getSession()).data.session;
      if (session?.access_token && currentUser?.id && paperId) {
        const objectiveTypes = ['single_choice', 'multi_choice', 'judge'];
        const wrongQuestions: any[] = [];

        for (const q of examQuestions) {
          if (!objectiveTypes.includes(q.type)) continue;
          const userAnswer = answers[q.id];
          if (!userAnswer) continue;

          let isWrong = false;
          if (q.type === 'multi_choice') {
            const selectedIds = userAnswer.split(',');
            const correctIds = q.options?.filter(o => o.isCorrect).map(o => o.id) || [];
            isWrong = !(selectedIds.length === correctIds.length && selectedIds.every(id => correctIds.includes(id)));
          } else {
            const selectedOption = q.options?.find(o => o.id === userAnswer);
            isWrong = !selectedOption?.isCorrect;
          }

          if (isWrong) {
            wrongQuestions.push({
              question_id: q.id,
              paper_id: paperId,
              organization_code: paper?.organization || '',
              category_code: paper?.category_type || '',
              level_text: paper?.level_text || '',
              question_type: q.type,
              user_answer: userAnswer  // 保存学员的答案
            });
          }
        }

        if (wrongQuestions.length > 0) {
          await fetch(`${getSupabaseUrl()}/functions/v1/user-management`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${session.access_token}`
            },
            body: JSON.stringify({
              action: 'add_wrong_question',
              questions: wrongQuestions
            })
          });
        }
      }
    } catch (error) {
      // 静默处理
    }

    // 记录首次答题结果（用于统计题目整体正确率，作为自由组卷难度判断依据）
    try {
      if (currentUser?.id) {
        const objectiveTypes = ['single_choice', 'multi_choice', 'judge'];
        const firstAttempts: Array<{ questionId: string; isCorrect: boolean; userAnswer?: string }> = [];
        for (const q of examQuestions) {
          if (!objectiveTypes.includes(q.type)) continue; // 仅客观题
          const userAnswer = answers[q.id];
          if (!userAnswer) continue;
          let isCorrect = false;
          const correctIds = q.options?.filter(o => o.isCorrect).map(o => o.id) || [];
          if (q.type === 'multi_choice') {
            const userIds = userAnswer.split(',');
            isCorrect = userIds.length === correctIds.length && userIds.every(id => correctIds.includes(id));
          } else {
            isCorrect = correctIds.includes(userAnswer);
          }
          firstAttempts.push({ questionId: q.id, isCorrect, userAnswer });
        }
        if (firstAttempts.length > 0) {
          await saveFirstAttempts(currentUser.id, firstAttempts);
        }
      }
    } catch (error) {
      console.error('记录首次答题失败:', error);
    }

    // 调用U币服务计算经验
    try {
      const session = (await supabase.auth.getSession()).data.session;
      if (session?.access_token && currentUser?.id && paperId) {
        const totalScore = paper?.total_score || examQuestions.reduce((sum, q) => sum + q.score, 0);
        const answerTimeSeconds = EXAM_DURATION - timeLeft;

        const { count } = await supabase
          .from('exam_records')
          .select('*', { count: 'exact' })
          .eq('user_id', currentUser.id)
          .eq('paper_id', paperId)
          .lt('created_at', new Date().toISOString());

        const isFirstSubmit = !count || count === 0;
      }
    } catch (error) {
      console.error('计算U币失败:', error);
    }

    // 跳转到测评结果页
    const typeStats = examQuestions.reduce((acc, q) => {
      const typeKey = q.type;
      if (!acc[typeKey]) acc[typeKey] = { total: 0, correct: 0, score: 0, totalScore: 0 };
      acc[typeKey].total++;
      acc[typeKey].totalScore += q.score;
      const userAnswer = answers[q.id];
      if (userAnswer) {
        const correctIds = q.options?.filter(o => o.isCorrect).map(o => o.id) || [];
        let isCorrect = false;
        if (q.type === 'multi_choice') {
          const userIds = userAnswer.split(',');
          isCorrect = userIds.length === correctIds.length && userIds.every(id => correctIds.includes(id));
        } else {
          isCorrect = correctIds.includes(userAnswer);
        }
        if (isCorrect) {
          acc[typeKey].correct++;
          acc[typeKey].score += q.score;
        }
      }
      return acc;
    }, {} as Record<string, { total: number; correct: number; score: number; totalScore: number }>);

    navigate(`/exam-result/${paperId || 'custom'}`, {
      state: {
        paperId: paperId || 'custom',
        paperTitle: paper?.title || '自定义组卷',
        score: finalScore,
        totalScore: paper?.total_score || examQuestions.reduce((sum, q) => sum + q.score, 0),
        timeLeft,
        examDuration: EXAM_DURATION,
        answers,
        questions: examQuestions,
        typeStats
      }
    });
  };

  const handleSubmit = () => {
    const unansweredCount = examQuestions.length - answeredCount;
    if (unansweredCount > 0) {
      if (!confirm(`您还有 ${unansweredCount} 道题未作答，确定要提交吗？`)) return;
    } else if (!confirm('确定要提交试卷吗？提交后将无法修改答案。')) return;
    confirmSubmit();
  };

  const handleRestart = async () => {
    if (!currentUser?.id) return;
    setIsSubmitted(false);
    setScore(0);
    setAnswers({});
    setMarkedQuestions(new Set());
    setCurrentIndex(0);
    setTimeLeft(EXAM_DURATION);
    clearLocalCache();
    await supabase.from('exam_progress').delete().eq('user_id', currentUser.id).eq('exam_id', examKey);
  };

  const toggleMark = () => {
    const newMarked = new Set(markedQuestions);
    if (newMarked.has(currentIndex)) newMarked.delete(currentIndex);
    else newMarked.add(currentIndex);
    setMarkedQuestions(newMarked);
  };

  // ============ 主观题"保存作答" ============
  // 与客观题自动保存机制一致：调用 saveNow() 立即写入 exam_progress 表
  // 答案已通过 handleTextAnswerChange 写入 answers state，此处仅触发立即持久化
  const handleSaveSubjectiveAnswer = async (question: Question) => {
    const textAnswer = answers[question.id] || answers[`${question.id}_text`] || '';
    if (!textAnswer.trim()) {
      showToast('请先输入答案后再保存', 'error');
      return;
    }
    if (isSavingAnswer) return;
    setIsSavingAnswer(true);
    try {
      // 立即保存到 exam_progress 表（与客观题自动保存走同一通道，避免重复保存）
      await saveNow();
      showToast('已保存作答', 'success');
    } catch {
      showToast('保存失败，请重试', 'error');
    } finally {
      setIsSavingAnswer(false);
    }
  };

  // ============ 答题变更处理（含防抖保存触发） ============

  const handleAnswerChange = (questionId: string, answer: string) => {
    if (answer) {
      setAnswers({ ...answers, [questionId]: answer });
    } else {
      const newAnswers = { ...answers };
      delete newAnswers[questionId];
      setAnswers(newAnswers);
    }
    // 触发防抖自动保存
    triggerDebouncedSave();
  };

  const handleTextAnswerChange = (questionId: string, text: string) => {
    if (text.trim()) {
      setAnswers({ ...answers, [questionId]: text, [`${questionId}_text`]: text });
    } else {
      const newAnswers = { ...answers };
      delete newAnswers[questionId];
      delete newAnswers[`${questionId}_text`];
      setAnswers(newAnswers);
    }
    // 触发防抖自动保存
    triggerDebouncedSave();
  };

  // ============ 工具函数 ============

  const formatTime = (s: number) => {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
  };

  const getQuestionTypeName = (type: string, subType?: string) => {
    if (type === 'scratch_coding') return categoryStore.getCategoryNameByCode('scratch') || '图形化编程';
    if (type === 'coding' && subType) return subTypeNames[subType] || '主观题';
    return typeNames[type] || '单选题';
  };

  // 预览模式：保存缩放设置到数据库
  const handleSaveWidthSettings = async () => {
    if (isSavingWidths) return;
    setIsSavingWidths(true);
    try {
      // 关键修复：通过 ref 读取最新的 examQuestions，避免闭包陷阱
      // 确保保存时包含所有修改（题干图片 + 选项图片）
      const latestExamQuestions = examQuestionsRef.current;
      for (const q of latestExamQuestions) {
        // 关键修复：对于 HTML 内嵌图片场景，需要同步更新 content 字段中的 <img style="width:N%">
        // 否则重新加载时 buildStandardFields 会优先从 content 提取旧宽度，忽略新的 title_image_widths
        let updatedContent = q.content || '';
        const widths = q.titleImageWidths || [];
        if (updatedContent.includes('<img') && widths.length > 0) {
          let imgIdx = 0;
          updatedContent = updatedContent.replace(/<img([^>]*?)>/g, (match, attrs) => {
            const width = widths[imgIdx] || 100;
            imgIdx++;
            // 移除已有 style 中的 width，注入新宽度
            if (/style="/i.test(attrs)) {
              return match.replace(/<img([^>]*?)>/, (_m, a: string) => {
                const updatedAttrs = a.replace(/style="([^"]*)"/i, (_sm: string, styleContent: string) => {
                  const cleanedStyle = styleContent.replace(/(?<!max-)width:\s*\d+%;?\s*/g, '');
                  return `style="width:${width}%;${cleanedStyle}"`;
                });
                return `<img${updatedAttrs}>`;
              });
            }
            return `<img${attrs} style="width:${width}%;">`;
          });
        }

        await supabase.from('questions').update({
          title_image_widths: q.titleImageWidths || [],
          title_image_layout: (q as any).titleImageLayout || 'auto',
          image_position: (q as any).imagePosition || 'below',
          option_image_widths: q.options?.reduce((acc: Record<string, number[]>, o: any) => {
            const optionImageWidths = o.imageWidths && o.imageWidths.length > 0
              ? o.imageWidths
              : (o.imageWidth ? [o.imageWidth] : []);
            if (optionImageWidths.length > 0) acc[o.id] = optionImageWidths;
            return acc;
          }, {}) || {},
          options: q.options || [],
          option_layout: (q as any).optionLayout || 'grid',
          // 关键修复：同步更新 content 字段，确保 HTML 内嵌图片的宽度持久化
          content: updatedContent
        }).eq('id', q.id);
      }
      showToast('保存成功', 'success');
      // 关键修复：同步更新内部 examQuestions state 的 content 字段
      // 这样下次重新进入预览时（即使不重新查询数据库）也能显示最新宽度
      // 因为 buildStandardFields 优先从 content 提取宽度，必须保证 content 与 titleImageWidths 一致
      const syncedQuestions = latestExamQuestions.map(q => {
        let updatedContent = q.content || '';
        const widths = q.titleImageWidths || [];
        if (updatedContent.includes('<img') && widths.length > 0) {
          let imgIdx = 0;
          updatedContent = updatedContent.replace(/<img([^>]*?)>/g, (match, attrs) => {
            const width = widths[imgIdx] || 100;
            imgIdx++;
            if (/style="/i.test(attrs)) {
              return match.replace(/<img([^>]*?)>/, (_m, a: string) => {
                const updatedAttrs = a.replace(/style="([^"]*)"/i, (_sm: string, styleContent: string) => {
                  const cleanedStyle = styleContent.replace(/(?<!max-)width:\s*\d+%;?\s*/g, '');
                  return `style="width:${width}%;${cleanedStyle}"`;
                });
                return `<img${updatedAttrs}>`;
              });
            }
            return `<img${attrs} style="width:${width}%;">`;
          });
        }
        return { ...q, content: updatedContent };
      });
      setExamQuestionsState(syncedQuestions);
      examQuestionsRef.current = syncedQuestions;

      const snapshot: Record<string, { titleImageWidths?: number[]; optionImageWidths?: { id: string; imageWidths?: number[] }[] }> = {};
      syncedQuestions.forEach((q: Question) => {
        snapshot[q.id] = {
          titleImageWidths: q.titleImageWidths ? [...q.titleImageWidths] : undefined,
          optionImageWidths: q.options?.map(o => {
            const optionImageWidths = o.imageWidths && o.imageWidths.length > 0
              ? o.imageWidths
              : (o.imageWidth ? [o.imageWidth] : []);
            return { id: o.id, imageWidths: optionImageWidths };
          })
        };
      });
      setInitialWidths(snapshot);
      setHasWidthChanges(false);
      // 编辑器预览模式下，同步变更回 PaperEditor state
      // 关键修复：确保同步触发父组件更新，避免异步 Promise 导致的竞态条件
      if (isEditorPreview && onExamQuestionsChange) {
        onExamQuestionsChange(syncedQuestions);
      }
    } catch (error) {
      showToast('保存失败，请重试', 'error');
    } finally {
      setIsSavingWidths(false);
    }
  };

  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    const toast = document.createElement('div');
    toast.className = `fixed top-20 left-1/2 -translate-x-1/2 z-50 px-4 py-2 rounded-lg shadow-lg text-sm font-medium transition-all duration-300 ${type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'}`;
    toast.textContent = message;
    document.body.appendChild(toast);
    setTimeout(() => {
      toast.style.opacity = '0';
      setTimeout(() => toast.remove(), 300);
    }, 2000);
  };

  // 获取本地缓存数据的辅助函数
  const getLocalCacheData = (): AutoSaveData | null => {
    try {
      const raw = localStorage.getItem(`autosave_${examKey}`);
      if (!raw) return null;
      const data = JSON.parse(raw) as AutoSaveData;
      const savedAt = new Date(data.savedAt);
      const hoursDiff = (Date.now() - savedAt.getTime()) / (1000 * 60 * 60);
      if (hoursDiff > 24) {
        localStorage.removeItem(`autosave_${examKey}`);
        return null;
      }
      return data;
    } catch {
      localStorage.removeItem(`autosave_${examKey}`);
      return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 h-16 lg:h-24 flex items-center justify-between">
          <div className="flex items-center gap-2 lg:gap-3 shrink-0">
            <button onClick={onBack} className="flex items-center gap-1 text-gray-600 hover:text-gray-900 transition-colors">
              <ChevronLeft className="w-4 h-4" />
              <span className="text-xs lg:text-sm">返回</span>
            </button>
            <button onClick={() => navigate('/')} className="flex items-center gap-1 text-gray-600 hover:text-gray-900 transition-colors">
              <Home className="w-4 h-4" />
              <span className="text-xs lg:text-sm hidden sm:inline">首页</span>
            </button>
          </div>
          <div className="flex flex-col items-center min-w-0 px-2">
            <h1 className="font-bold text-gray-900 text-sm sm:text-base lg:text-2xl truncate max-w-[200px] sm:max-w-[400px] lg:max-w-none">
              {paper?.title || '在线考试'}
            </h1>
          </div>
          <div className="flex items-center gap-2 lg:gap-4 shrink-0">
            {isSubmitted && (
              <div className="flex items-center gap-1 lg:gap-2 px-2 lg:px-4 py-1 lg:py-2 rounded-lg border bg-red-50 border-red-200">
                <span className="text-red-600 font-bold text-sm lg:text-xl">得分：{score}分</span>
              </div>
            )}
            {/* 自动保存状态指示器 - 预览模式下隐藏 */}
            {!isPreview && !isEditorPreview && (
              <AutoSaveStatusIndicatorInline statusInfo={statusInfo} />
            )}
            {/* 倒计时 - 预览模式置灰 */}
            <div className={`flex items-center gap-1 lg:gap-2 px-2 lg:px-4 py-1 lg:py-2 rounded-lg border ${
              isPreview
                ? 'bg-gray-100 border-gray-300 text-gray-400 opacity-60'
                : isTimeWarning
                  ? 'bg-red-50 border-red-200 text-red-700'
                  : 'bg-white border-blue-200 text-blue-700'
            }`}>
              <Clock className={`w-3 h-3 lg:w-4 lg:h-4 ${!isPreview && isTimeWarning ? 'animate-spin' : ''}`} />
              <span className="font-mono font-bold text-xs lg:text-base">
                {isSubmitted ? (() => {
                  const usedTime = EXAM_DURATION - timeLeft;
                  const h = Math.floor(usedTime / 3600);
                  const m = Math.floor((usedTime % 3600) / 60);
                  const s = usedTime % 60;
                  if (h > 0) return `用时：${h}小时${m}分${s}秒`;
                  return `用时：${m}分${s}秒`;
                })() : formatTime(timeLeft)}
              </span>
            </div>
            {/* 提交试卷按钮 / 保存修改按钮 */}
            {isEditorPreview ? (
              <button
                onClick={handleSaveWidthSettings}
                disabled={!hasWidthChanges || isSavingWidths}
                className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                  hasWidthChanges && !isSavingWidths
                    ? 'bg-emerald-500 text-white hover:bg-emerald-600'
                    : 'bg-gray-300 text-white cursor-not-allowed'
                }`}
              >
                {isSavingWidths ? '保存中...' : '保存修改'}
              </button>
            ) : !isSubmitted ? (
              <button
                onClick={handleSubmit}
                className="px-6 py-2 rounded-lg font-medium bg-red-500 text-white hover:bg-red-600 transition-colors"
              >
                提交试卷
              </button>
            ) : (
              <button onClick={() => { if (confirm('确定要重新测试吗？当前答题记录将被清空。')) handleRestart(); }} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium">
                重新测试
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3 sm:py-6">
        {/* Mobile Answer Card Button */}
        <button onClick={() => setShowSidebar(true)} className="lg:hidden fixed bottom-4 right-4 z-40 w-12 h-12 bg-blue-600 text-white rounded-full shadow-lg hover:bg-blue-700 transition-colors flex items-center justify-center">
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="3" width="7" height="7" rx="1" />
            <rect x="14" y="3" width="7" height="7" rx="1" />
            <rect x="3" y="14" width="7" height="7" rx="1" />
            <rect x="14" y="14" width="7" height="7" rx="1" />
          </svg>
        </button>

        <div className="flex gap-4 lg:gap-6">
          {/* Sidebar - Answer Card - 固定不动 */}
          <div className="hidden lg:block w-[calc(25%-1.5rem)] shrink-0">
            <div className="sticky top-[calc(4rem+1px)]">
            <AnswerCard
              examQuestions={examQuestions}
              currentIndex={currentIndex}
              answers={answers}
              markedQuestions={markedQuestions}
              isSubmitted={isSubmitted}
              paper={paper}
              onQuestionSelect={setCurrentIndex}
            />
            </div>
          </div>

          {/* Question Area */}
          <div className="flex-1 min-w-0 space-y-3 sm:space-y-4">
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
              {/* Question Header */}
              <div className="sticky lg:top-[calc(4rem+1px)] z-30 bg-gradient-to-r from-blue-50 to-blue-100 p-3 sm:p-4 lg:p-6 border-b border-blue-200">
                <div className="flex items-center justify-between gap-2 sm:gap-4">
                  <div className="flex items-center gap-2 lg:gap-3 shrink-0">
                    <span className="px-2 py-0.5 lg:px-3 lg:py-1 bg-blue-600 text-white text-xs lg:text-sm font-medium rounded-full">
                      {getQuestionTypeName(currentQuestion?.type, currentQuestion?.subType)}
                    </span>
                  </div>
                  <div className="hidden sm:flex items-center gap-2">
                    <button onClick={() => setCurrentIndex(Math.max(0, currentIndex - 1))} disabled={currentIndex === 0} className="px-3 lg:px-4 py-1.5 lg:py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-40 flex items-center gap-1.5 text-xs lg:text-sm font-medium transition-all">
                      <ChevronLeft className="w-3 h-3 lg:w-4 lg:h-4" />上一题
                    </button>
                    <button onClick={() => setCurrentIndex(Math.min(examQuestions.length - 1, currentIndex + 1))} disabled={currentIndex === examQuestions.length - 1} className="px-3 lg:px-4 py-1.5 lg:py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-40 flex items-center gap-1.5 text-xs lg:text-sm font-medium transition-all">
                      下一题<ChevronLeft className="w-3 h-3 lg:w-4 lg:h-4 rotate-180" />
                    </button>
                  </div>
                  <div className="flex items-center gap-1.5 lg:gap-2 ml-auto">
                    {!isSubmitted && (
                      <button
                        onClick={toggleMark}
                        className={`px-3 py-2 rounded-lg font-medium transition-all duration-200 flex items-center gap-1.5 text-sm ${
                          markedQuestions.has(currentIndex)
                            ? 'bg-amber-100 text-amber-700 border border-amber-300 hover:bg-amber-200'
                            : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {markedQuestions.has(currentIndex) ? <><BookmarkCheck className="w-4 h-4" />已标记</> : <><Bookmark className="w-4 h-4" />标记</>}
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Question Content */}
              <div className="p-3 sm:p-4 lg:p-6 border-b border-gray-100">
                <QuizQuestionTitle
                  question={currentQuestion}
                  currentIndex={currentIndex}
                  examQuestions={examQuestions}
                  onExamQuestionsChange={canScaleImages ? setExamQuestions : undefined}
                  onHasWidthChangesChange={canScaleImages ? setHasWidthChanges : undefined}
                  isPreview={canScaleImages}
                  canScaleImages={canScaleImages}
                  showCodingTitle={true}
                />
              </div>

              {/* Answer Area */}
              <div className="p-3 sm:p-4 lg:p-6">
                {/* 预览模式选项图片控制条 - 仅客观题且选项含图片时显示 */}
                {isPreview && (
                  <PreviewControls
                    question={currentQuestion}
                    examQuestions={examQuestions}
                    onExamQuestionsChange={setExamQuestions}
                    onHasWidthChangesChange={setHasWidthChanges}
                  />
                )}

                <QuestionRenderer
                  key={currentQuestion?.id}
                  question={currentQuestion}
                  currentIndex={currentIndex}
                  answers={answers}
                  isSubmitted={isSubmitted}
                  isPreview={isPreview}
                  paperId={paperId}
                  paper={paper}
                  currentUser={currentUser}
                  examQuestions={examQuestions}
                  isAssignmentMode={isAssignmentMode}
                  answerMode={answerMode}
                  onAnswerChange={handleAnswerChange}
                  onTextAnswerChange={handleTextAnswerChange}
                  onSaveAnswer={handleSaveSubjectiveAnswer}
                  isSavingAnswer={isSavingAnswer}
                  onExamQuestionsChange={setExamQuestions}
                  onHasWidthChangesChange={setHasWidthChanges}
                  enableImageScaling={canScaleImages}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Sidebar */}
      <AnimatePresence>
        {showSidebar && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 bg-black/50 z-40 lg:hidden"
              onClick={() => setShowSidebar(false)}
            />
            <motion.div initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }} transition={{ type: 'tween', duration: 0.3 }} className="fixed inset-y-0 right-0 w-[85vw] sm:w-80 bg-white shadow-xl z-50 lg:hidden">
              <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                <h2 className="font-bold text-gray-900">答题卡</h2>
                <button onClick={() => setShowSidebar(false)} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5 text-gray-600" /></button>
              </div>
              <div className="p-4 overflow-y-auto">
                <AnswerCard
                  examQuestions={examQuestions}
                  currentIndex={currentIndex}
                  answers={answers}
                  markedQuestions={markedQuestions}
                  isSubmitted={isSubmitted}
                  paper={paper}
                  onQuestionSelect={setCurrentIndex}
                  onCloseSidebar={() => setShowSidebar(false)}
                />
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* 试卷属性弹窗 */}
      {showPaperSettingsModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                试卷属性
              </h3>
              <button onClick={() => setShowPaperSettingsModal(false)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <div className="px-6 py-5 space-y-4">
              {/* 年份 */}
              <div className="flex items-center gap-4">
                <span className="text-sm text-gray-600 w-20 shrink-0">年份:</span>
                <select value={paper.year || ''} disabled className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-700">
                  <option value="">请选择</option>
                  {Array.from({ length: 21 }, (_, i) => String(2010 + i)).map(year => (
                    <option key={year} value={year}>{year}年</option>
                  ))}
                </select>
              </div>
              {/* 月份 */}
              <div className="flex items-center gap-4">
                <span className="text-sm text-gray-600 w-20 shrink-0">月份:</span>
                <select value={paper.month || ''} disabled className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-700">
                  <option value="">请选择</option>
                  <option value="0">空</option>
                  {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                    <option key={month} value={String(month)}>{month}月</option>
                  ))}
                </select>
              </div>
              {/* 考评体系 */}
              <div className="flex items-center gap-4">
                <span className="text-sm text-gray-600 w-20 shrink-0">考评体系:</span>
                <select value={paper.organization || ''} disabled className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-700">
                  <option value="">请选择</option>
                  <option value="电子学会">电子学会</option>
                  <option value="工信部">工信部</option>
                  <option value="中国电子学会">中国电子学会</option>
                </select>
              </div>
              {/* 课程分类 */}
              <div className="flex items-center gap-4">
                <span className="text-sm text-gray-600 w-20 shrink-0">课程分类:</span>
                <select value={paper.category_type || ''} disabled className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-700">
                  <option value="">请选择</option>
                  <option value="图形化编程">图形化编程</option>
                  <option value="Python编程">Python编程</option>
                  <option value="C++编程">C++编程</option>
                </select>
              </div>
              {/* 级别分类 */}
              <div className="flex items-center gap-4">
                <span className="text-sm text-gray-600 w-20 shrink-0">级别分类:</span>
                <select value={paper.level_text || ''} disabled className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-700">
                  <option value="">请选择</option>
                  <option value="一级">一级</option>
                  <option value="二级">二级</option>
                  <option value="三级">三级</option>
                  <option value="四级">四级</option>
                  <option value="五级">五级</option>
                  <option value="六级">六级</option>
                </select>
              </div>
              {/* 试卷类型 */}
              <div className="flex items-center gap-4">
                <span className="text-sm text-gray-600 w-20 shrink-0">试卷类型:</span>
                <select value={paper.source || ''} disabled className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-700">
                  <option value="">请选择</option>
                  <option value="真题">真题</option>
                  <option value="模拟题">模拟题</option>
                </select>
              </div>
              {/* 免费/付费 */}
              <div className="flex items-center gap-4">
                <span className="text-sm text-gray-600 w-20 shrink-0">免费/付费:</span>
                <select value={paper.nature || ''} disabled className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-700">
                  <option value="">请选择</option>
                  <option value="免费">免费</option>
                  <option value="付费">付费</option>
                </select>
              </div>
              {/* 考试时长 */}
              <div className="flex items-center gap-4">
                <span className="text-sm text-gray-600 w-20 shrink-0">考试时长:</span>
                <select value={paper.duration || '60'} disabled className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-700">
                  <option value="">请选择</option>
                  <option value="30">30分钟</option>
                  <option value="60">60分钟</option>
                  <option value="90">90分钟</option>
                  <option value="120">120分钟</option>
                  <option value="150">150分钟</option>
                  <option value="180">180分钟</option>
                </select>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => setShowPaperSettingsModal(false)}
                className="px-5 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm font-medium"
              >
                关闭
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
